import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import moment from 'moment-timezone';
import { Organizations } from '../../components/shared/http/request-interfaces';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Organization } from '../../models/entities/organization';
import { Unit } from '../../models/entities/unit';
import { OrganizationPreference } from 'app/models/entities/organization-preference';
import { DashboardWidget } from 'app/models/entities/dashboard-widget';
import { CustomField } from '../../models/entities/custom-field';
import { GtmService } from '../gtm.service';
import { BaseAvatar } from '../../models/entities/base/avatar';
import Moment = moment.Moment;
import CopyOrganizationContentRequest = Organizations.CopyOrganizationContentRequest;

export class SimpleOrganization extends BaseAvatar
{
    id: number;
    parentOrganizationId: number;
    propertyManagementCompanyId: number;
    name: string;
    checked: boolean = false;
    units: Unit[];
    deletedAt: Moment;
    demo: boolean;

    activeLaunchpad: boolean;
    visible: boolean;

    constructor(org: any) {
        super(org);

        if (org.id) {
            this.id = org.id;
        }
        if (org.deletedAt) {
            const timezone = LegFiJwtService.getTimezone();
            this.deletedAt = moment.utc(org.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (org.parentOrganizationId) {
            this.parentOrganizationId = org.parentOrganizationId;
        }

        if (org.propertyManagementCompanyId) {
            this.propertyManagementCompanyId = org.propertyManagementCompanyId;
        }

        if (org.name) {
            this.name = org.name;
        }

        if (org.units) {
            this.units = org.units.map((unit: any) => {
                return new Unit(unit);
            });
        }

        if (org.createdAt) {
            this.activeLaunchpad = moment().add(-30, 'days').isBefore(moment(org.createdAt));
        }

        if (org.demo) {
            this.demo = org.demo;
            this.name += ' • Test Data';
        }

        // managing visibility in the v2 layout header-menu
        this.visible = false;
    }
}

export interface PatchOrganizationRequest
{
    address1?: string;
    address2?: string;
    city?: string;
    country?: string;
    ein?: string;
    email?: string;
    fax?: string;
    fiscalPeriodStart?: string;
    invoiceReturnAddress1?: string;
    invoiceReturnAddress2?: string;
    invoiceReturnCity?: string;
    invoiceReturnState?: string;
    invoiceReturnZip?: string;
    name?: string;
    phone?: string;
    state?: string;
    timezone?: string;
    zip?: string;
}

export interface UpdateOrganizationPreferencesRequest
{
    admin_daily_summary_emails_enabled?: boolean;
    alliance_lockbox_auto_import_enabled?: boolean;
    alliance_lockbox_enabled?: boolean;
    alliance_lockbox_id?: string;
    alliance_lockbox_import_after?: string;
    alliance_lockbox_pmc_id?: string;
    allow_custom_email_send?: boolean;
    allow_phone_call_send?: boolean;
    allow_sms_send?: boolean;
    attach_pdf_invoice_to_statement_emails?: boolean;
    auto_tag_members_without_email?: boolean;
    auto_tag_members_without_phone?: boolean;
    auto_tag_paperless_units?: boolean;
    auto_tag_units_without_email?: boolean;
    auto_tag_units_without_phone?: boolean;
    auto_tag_units_no_registered_owner?: boolean;
    amenities_module_enabled?: boolean;
    bill_pay_bank_account_adding_enabled?: boolean;
    calendar_module_enabled?: boolean;
    include_all_owners_at_same_address_on_invoices?: boolean;
    manual_bank_account_adding_enabled?: boolean;
    members_without_email_tag?: string;
    members_without_phone_tag?: string;
    membership_directory_enabled?: boolean;
    message_board_module_enabled?: boolean;
    monthly_statement_emails_day_to_send?: number;
    monthly_statement_emails_enabled?: boolean;
    new_charge_emails_enabled?: boolean;
    paperless_units_tag?: string;
    past_due_emails_days_past_due?: number;
    past_due_emails_enabled?: boolean;
    payables_module_enabled?: boolean;
    payments_pay_fees_first?: boolean;
    reminder_emails_days_before_due?: number;
    reminder_emails_enabled?: boolean;
    send_monthly_statements_to_autopay_members?: boolean;
    send_new_charges_to_autopay?: boolean;
    send_new_late_fee_charge_emails?: boolean;
    surveys_module_enabled?: boolean;
    units_without_email_tag?: string;
    units_without_phone_tag?: string;
    units_no_registered_owner_tag?: string;
    use_cash_accounting?: boolean;
    use_charge_due_date_for_prepaids?: boolean;
    violations_module_enabled?: boolean;
    voting_module_enabled?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class OrganizationService
{
    private _preferencesSubject = new BehaviorSubject<OrganizationPreference | null>(null);
    public preferences$ = this._preferencesSubject.asObservable();

    constructor(
            private _http: ApplicationHttpClient,
            private _gtm: GtmService,
    ) {
    }

    /**
     * Returns the current cached preferences value (may be null if not yet loaded).
     *
     * Note: This cache may become stale if preferences are updated elsewhere.
     * Switching orgs triggers a dashboard navigation which refreshes preferences.
     * If fresh data is critical, call getOrganizationById with ['preferences'] instead.
     */
    get currentPreferences(): OrganizationPreference | null {
        return this._preferencesSubject.getValue();
    }

    /**
     * Fetches the list of DashboardWidgets for the current org and user
     */
    public getOrgDashboardWidgets(): Observable<DashboardWidget[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationDashboardWidgets(jwt.orgId, jwt.id));
        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((widget) => {
                return new DashboardWidget(widget);
            });
        }));
    }

    /**
     * Updates dashboard widget ordering and enabled state
     */
    public updateOrgDashboardWidgetOrder(data: {
        id: number,
        rank: number,
        isEnabled: boolean
    }[]): Observable<DashboardWidget[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationDashboardWidgets(jwt.orgId, jwt.id));
        return this._http.post(url, JSON.stringify({widgets: data})).pipe(map((response: Object[]) => {
            return response.map((widget) => {
                return new DashboardWidget(widget);
            });
        }));
    }

    /**
     * @param {number} organizationId
     * @param {string[]} withs
     * @returns {Observable<Organization>}
     */
    public getOrganizationById(organizationId: number, withs: string[]): Observable<Organization> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const withString = withs.length > 0 ? '?with=' + withs.join(',') : '';
        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Organization(organizationId || jwt.orgId)) + withString;

        return this._http.get(url).pipe(
            map((response: Object) => {
                return new Organization(response);
            }),
            tap((organization: Organization) => {
                // Cache preferences if they were included in the response
                if (withs.includes('preferences') && organization.preferences) {
                    this._preferencesSubject.next(organization.preferences);
                }
            }),
        );
    }

    /**
     * Whether the organization can currently accept homeowner online payments.
     *
     * Returns false for Treasury organizations that have not completed KYC (no deposit
     * account provisioned yet), so the Make Payment flow can be blocked up front instead
     * of failing at checkout. Non-Treasury organizations always return true.
     *
     * @param {number} organizationId
     * @returns {Observable<{ acceptsOnlinePayments: boolean }>}
     */
    public getPaymentReadiness(organizationId: number): Observable<{ acceptsOnlinePayments: boolean }> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.TreasuryModule.PaymentReadiness(organizationId || jwt.orgId));

        return this._http.get<{ acceptsOnlinePayments: boolean }>(url);
    }

    /**
     * @param {number} organizationId
     * @returns {Observable<Organization>}
     */
    public getOrganizationSimpleInfoById(organizationId: number): Observable<Organization> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.SimpleOrgInfo(organizationId || jwt.orgId));

        return this._http.get(url).pipe(map((response: Object) => {
            return new Organization(response);
        }));
    }

    public getStripeLink(orgId: number, accountId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgBankAccount(orgId || jwt.orgId, accountId) + '/verify/link',
        );

        return this._http.get(url);
    }

    /**
     * Updates membership details from Core.
     * @param orgId
     * @param requestBody
     * @returns {Observable<Organization>}
     */
    public updateOrganization(orgId: number, requestBody: PatchOrganizationRequest): Observable<Organization> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                    Routes.LegFiCore.Organization(orgId || jwt.orgId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map((response: Object) => {
            return new Organization(response);
        }));
    }

    /**
     * Updates membership details from Core.
     * @param requestBody
     * @returns {Observable<Organization>}
     */
    public updateOrganizationPreferences(requestBody: UpdateOrganizationPreferencesRequest): Observable<OrganizationPreference> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.OrganizationPreferences(jwt.orgId),
                ),
                JSON.stringify(requestBody),
        ).pipe(map((response: Object) => {
            const preferences = new OrganizationPreference(response);
            this._preferencesSubject.next(preferences);
            return preferences;
        }));
    }

    /**
     * @param {number} organizationId
     * @param {{paymentMethodType:string, paymentMethodId:number}} paymentMethod
     * @returns {Observable<Organization>}
     */
    public updatePaymentMethod(
            organizationId: number,
            paymentMethod: { paymentMethodType: string, paymentMethodId: number },
    ): Observable<Organization> {

        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgDefaultPaymentMethod(organizationId || jwt.orgId),
        );

        return this._http.put(url, JSON.stringify(paymentMethod)).pipe(map((response: Object) => {
            return new Organization(response);
        }));
    }

    public fixOrgExpiration(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrganizationExpirationCheckout(organizationId || jwt.orgId),
        );

        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * @param {number} organizationId
     * @returns {Observable<Object>}
     */
    public getDashboardSummary(organizationId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.OrgDashboard(organizationId || jwt.orgId),
        );

        return this._http.get(url);
    }

    /**
     * @param {number} organizationId
     * @param {any} options
     * @returns {Observable<Object>}
     */
    public getCollectionProgress(organizationId: number, options?: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params: any[] = [];
        if (options) {
            for (const prop in options) {
                if (options.hasOwnProperty(prop)) {
                    params.push(prop + '=' + options[prop]);
                }
            }
        }
        const queryString: string = params.length > 0 ? '?' + params.join('&') : '';

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.CollectionProgress(organizationId || jwt.orgId) + queryString,
        );

        return this._http.get(url);
    }

    /**
     * @param {number} organizationId
     * @param {any} options
     * @returns {Observable<Object>}
     */
    public getPayments(organizationId: number, options?: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const params: any[] = [];
        if (options) {
            for (const prop in options) {
                if (options.hasOwnProperty(prop)) {
                    params.push(prop + '=' + options[prop]);
                }
            }
        }
        const queryString: string = params.length > 0 ? '?' + params.join('&') : '';

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.PaymentsGraph(organizationId || jwt.orgId) + queryString,
        );

        return this._http.get(url);
    }


    /**
     * @returns {Observable<SimpleOrganization[]>}
     */
    public getSimpleOrgList(userId?: number, filtered?: boolean): Observable<SimpleOrganization[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.SimpleOrgList(),
        );
        if (userId !== null) {
            url = url + '?userId=' + userId;
            if (filtered) {
                url = url + '&filtered=true';
            }
        } else {
            if (filtered) {
                url = url + '?filtered=true';
            }
        }

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((org) => {
                return new SimpleOrganization(org);
            });
        }));
    }

    /**
     * @param {string} term
     * @returns {Observable}
     */
    public searchOrgsByName(term: string): Observable<any[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.SearchOrgList(),
        );
        if (term) {
            url = url + '?term=' + term;
        }

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((org) => {
                return new SimpleOrganization(org);
            });
        }));
    }

    /**
     * Bulk imports currently available for transactions, members
     * @param {number} organizationId
     * @param {string} whichModel
     * @returns {Observable<Object>}
     */
    public getSupportedFieldsForBulkImport(organizationId: number, whichModel: string): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url: string = '';
        if (whichModel === 'transaction') {
            url = Routes.MakeLegFiCoreUrl(
                    Routes.LegFiCore.TransactionsBulkImportSupportedColumns(organizationId || jwt.orgId),
            );
        } else if (whichModel === 'unit') {
            url = Routes.MakeLegFiCoreUrl(
                    Routes.LegFiCore.UnitsBulkImportSupportedColumns(organizationId || jwt.orgId),
            );
        }

        return this._http.get(url);
    }

    /**
     * @param type entity e.g. 'member' 'unit' 'vendor'
     * @returns {Observable<Object>}
     */
    /**
     * The org's member + unit custom field names formatted as "Insert Field" tokens
     * for the email composer (e.g. "Member Custom Field: Kids Names"). Fields come
     * back from the API sorted by name within each scope. Excluded: fields hidden
     * from homeowners (recipients are homeowners, so email must respect the same
     * visibility the portal enforces) and names containing braces (they can't form
     * an unambiguous {token}).
     */
    public getCustomFieldInsertTokens(): Observable<string[]> {
        const insertable = (field: CustomField) => field.ownersCanView && !/[{}]/.test(field.name);
        return forkJoin([
            this.getCustomFieldsForOrganization('member'),
            this.getCustomFieldsForOrganization('unit'),
        ]).pipe(map(([memberFields, unitFields]: [CustomField[], CustomField[]]) => [
            ...memberFields.filter(insertable).map((field) => `Member Custom Field: ${field.name}`),
            ...unitFields.filter(insertable).map((field) => `Unit Custom Field: ${field.name}`),
        ]));
    }

    public getCustomFieldsForOrganization(type: string): Observable<CustomField[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.CustomFieldsForOrg(jwt.orgId, type),
        );

        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((field) => new CustomField(field));
        }));
    }

    /**
     * @param type entity e.g. 'member' 'unit' 'vendor'
     * @param {Object} requestBody
     * @returns {Observable<Object>}
     */
    public createCustomFieldForOrganization(type: string, requestBody: { name: string }): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.CustomFieldsForOrg(jwt.orgId, type),
                ),
                JSON.stringify(requestBody),
        );
    }

    /**
     * @param type entity e.g. 'member' 'unit' 'vendor'
     * @param fieldId
     * @param requestBody
     */
    public editCustomFieldForOrganization(
            type: string,
            fieldId: number,
            requestBody: { name: string , owners_can_view: boolean , owners_can_edit: boolean},
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.CustomFieldForOrg(jwt.orgId, type, fieldId),
        );

        return this._http.patch(url, JSON.stringify(requestBody));
    }

    /**
     * @param type entity e.g. 'member' 'unit' 'vendor'
     * @param {number} fieldId
     * @returns {Observable<Object>}
     */
    public deleteCustomFieldForOrganization(type: string, fieldId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(
                Routes.LegFiCore.CustomFieldForOrg(jwt.orgId, type, fieldId),
        );

        return this._http.delete(url);
    }


    /**
     * @param orgId
     * @param reason
     * @returns {any}
     */
    public deleteOrganization(orgId: number, reason: string = null): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Organization(orgId)) + '?reason=' + encodeURIComponent(reason);

        return this._http.delete(url);
    }

    /**
     * @param orgId
     * @returns {any}
     */
    public restoreOrganization(orgId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.RestoreOrganization(orgId));

        return this._http.post(url, '');
    }

    /**
     * @param {number} orgId
     * @param {any} request
     * @returns {any}
     */
    public sendStatements(orgId: number, request: any): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Organization(orgId || jwt.orgId)) + '/send-statements';

        return this._http.post(url, JSON.stringify(request));
    }

    /**
     * Updates just the has_logo setting for this org
     * @param {number} orgId
     * @returns {Observable}
     */
    public deleteOrganizationLogo(orgId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        return this._http.patch(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.DeleteOrganizationLogo(orgId || jwt.orgId),
                ),
                JSON.stringify(orgId),
        );
    }

    /**
     * @param {number} orgId
     * @param {string} token
     * @returns {Observable<Object>}
     */
    public verifyOrgSignup(orgId: number, token: string): Observable<Object> {
        const body = {vtoken: token};
        return this._http.post(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.VerifyOrganization(orgId),
                ),
                JSON.stringify(body),
        );
    }

    verifyAbilityToSendMail(orgId: number): Observable<any> {
        return this._http.get(
                Routes.MakeLegFiCoreUrl(
                        Routes.LegFiCore.Organization(orgId) + '/mail/verify',
                ),
        );
    }

    public unenrollFromLockbox(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationLockbox(jwt.orgId));

        return this._http.delete(url);
    }

    public copyOrgContent(sourceOrgId: number, request: CopyOrganizationContentRequest): Observable<void> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.Organization(sourceOrgId)) + '/copy-content';

        return this._http.post(url, JSON.stringify(request));
    }

    public getOrganizationReplyToEmail(orgId: number): Observable<string> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrganizationReplyToEmail(orgId || jwt.orgId));

        return this._http.get(url).pipe(map((response: {replyToEmail: string}) => {
            return response.replyToEmail;
        }));
    }
}
